import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import AttachmentList from "../components/AttachmentList";
import { EmailBody } from "../components/EmailBody";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Search, User } from "lucide-react";

export default function TrackTicket() {
  const [ticketNumber, setTicketNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTicket(null);
    try {
      const { data } = await api.get(`/tickets/track/${encodeURIComponent(ticketNumber.trim())}`);
      setTicket(data);
    } catch (err) {
      toast.error("No matching ticket", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-6">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight font-display">
          Track your ticket.
        </h1>
        <p className="mt-2 text-sm text-slate-600">Enter the ticket number we sent you.</p>
      </div>

      <form onSubmit={lookup} className="beacon-card p-6 sm:p-8 shadow-md space-y-4">
        <div>
          <label className="beacon-label">Ticket number</label>
          <input
            required
            className="beacon-input font-mono uppercase tracking-widest text-center text-lg font-bold py-3"
            placeholder="TKT-XXXXXXXX"
            data-testid="ticket-lookup-input"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value.toUpperCase())}
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="ticket-lookup-search-btn"
          className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all"
        >
          <Search className="w-4 h-4 mr-2" />
          {loading ? "Looking up..." : "Look up ticket"}
        </Button>
      </form>

      {ticket && (
        <div className="mt-8 beacon-card p-6 sm:p-8 shadow-lg" data-testid="ticket-tracker-result">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-slate-500">
                {ticket.ticket_number}
              </div>
              <div className="mt-1 text-xl font-bold text-slate-900 font-display">{ticket.subject}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{ticket.customer_name}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
            </div>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Category</div>
              <div className="mt-1 font-semibold text-slate-800 capitalize">{ticket.category.replace("_", " ")}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Assigned to</div>
              <div className="mt-1 font-semibold text-slate-800">{ticket.assignee_name || "Not yet assigned"}</div>
            </div>
          </div>

          <div className="mt-5 rounded-lg bg-white border border-slate-200 p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Original message</div>
            <div className="mt-2">
              <EmailBody text={ticket.message} html={ticket.message_html} />
            </div>
          </div>

          {(ticket.attachments || []).length > 0 && (
            <div className="mt-4">
              <AttachmentList
                attachments={ticket.attachments}
                email={ticket.customer_email}
                testid="track-attachments"
              />
            </div>
          )}

          {(ticket.replies || []).length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 font-bold">
                Replies from support
              </div>
              {ticket.replies.map((r) => (
                <div key={r.id} className="rounded-lg bg-blue-50/60 border border-blue-100 p-4" data-testid={`track-reply-${r.id}`}>
                  <div className="text-[11px] text-blue-800 font-semibold">
                    {r.author_name}
                    <span className="text-blue-700/70 font-normal"> · {new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1">
                    <EmailBody text={r.body} html={r.body_html} />
                  </div>
                  {(r.attachments || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.attachments.map((a) => (
                        <a
                          key={a.id}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${a.id}?email=${encodeURIComponent(ticket.customer_email)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs bg-white border border-blue-200 hover:border-blue-500 rounded-md px-2 py-1 text-blue-700"
                          data-testid={`track-reply-attachment-${a.id}`}
                        >
                          {a.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 text-xs text-slate-500">
            Created {new Date(ticket.created_at).toLocaleString()} · Updated {new Date(ticket.updated_at).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}
